import type { Currency, DistanceUnit } from '@/types'
import { convertCurrency, formatCurrency } from './currency'

/**
 * Format distance based on user preference (km or mi)
 * @param meters Distance in meters (base unit in database)
 * @param unit Target unit ('km' or 'mi')
 * @param decimals Number of decimal places (default: 0 for km, 1 for mi)
 * @returns Formatted distance string with unit
 */
export function formatDistance(
  meters: number,
  unit: DistanceUnit = 'km',
  decimals?: number
): string {
  if (!meters || meters === 0) {
    return `0 ${unit}`
  }

  let distance: number
  let defaultDecimals: number

  if (unit === 'mi') {
    // Convert meters to miles (1 mile = 1609.34 meters)
    distance = meters / 1609.34
    defaultDecimals = 1
  } else {
    // Convert meters to kilometers
    distance = meters / 1000
    defaultDecimals = 0
  }

  const finalDecimals = decimals !== undefined ? decimals : defaultDecimals

  // For large distances, use 'k' suffix (e.g., "1.2k km")
  if (distance >= 1000) {
    return `${(distance / 1000).toFixed(1)}k ${unit}`
  }

  return `${distance.toFixed(finalDecimals)} ${unit}`
}

/**
 * Format duration in minutes to human-readable format
 * @param minutes Duration in minutes
 * @param shortFormat Use short format (e.g., "2h 30m" instead of "2 hours 30 minutes")
 * @returns Formatted duration string
 */
export function formatDuration(minutes: number, shortFormat: boolean = true): string {
  if (!minutes || minutes === 0) {
    return shortFormat ? '0m' : '0 minutes'
  }

  const hours = Math.floor(minutes / 60)
  const mins = Math.round(minutes % 60)

  if (shortFormat) {
    if (hours > 0 && mins > 0) {
      return `${hours}h ${mins}m`
    } else if (hours > 0) {
      return `${hours}h`
    } else {
      return `${mins}m`
    }
  } else {
    const hourText = hours === 1 ? 'hour' : 'hours'
    const minText = mins === 1 ? 'minute' : 'minutes'

    if (hours > 0 && mins > 0) {
      return `${hours} ${hourText} ${mins} ${minText}`
    } else if (hours > 0) {
      return `${hours} ${hourText}`
    } else {
      return `${mins} ${minText}`
    }
  }
}

/**
 * Format number with thousands separator
 * @param num Number to format
 * @param decimals Number of decimal places (default: 0)
 * @returns Formatted number string
 */
export function formatNumber(num: number, decimals: number = 0): string {
  return new Intl.NumberFormat('default', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  }).format(num)
}

/**
 * Format cost from EUR (database base currency) to user's preferred currency
 * @param amountInEUR Amount in EUR
 * @param targetCurrency User's preferred currency
 * @returns Formatted currency string
 */
export function formatCostFromEUR(amountInEUR: number, targetCurrency: Currency): string {
  if (!amountInEUR || amountInEUR === 0) {
    return formatCurrency(0, targetCurrency)
  }

  const converted = convertCurrency(amountInEUR, 'EUR', targetCurrency)
  return formatCurrency(converted, targetCurrency)
}

/**
 * Format date to localized string
 * @param date Date object or string
 * @param format Format type ('short', 'medium', 'long')
 * @returns Formatted date string
 */
export function formatDate(
  date: Date | string,
  format: 'short' | 'medium' | 'long' = 'medium'
): string {
  const d = typeof date === 'string' ? new Date(date) : date

  if (isNaN(d.getTime())) {
    return 'Invalid date'
  }

  let options: Intl.DateTimeFormatOptions

  if (format === 'short') {
    options = { year: 'numeric', month: 'short', day: 'numeric' }
  } else if (format === 'long') {
    options = {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }
  } else {
    options = { year: 'numeric', month: 'long', day: 'numeric' }
  }

  return new Intl.DateTimeFormat('default', options).format(d)
}

/**
 * Convert distance from meters to target unit
 * @param meters Distance in meters
 * @param unit Target unit ('km' or 'mi')
 * @returns Distance in target unit
 */
export function convertDistance(meters: number, unit: DistanceUnit): number {
  if (unit === 'mi') {
    return meters / 1609.34
  }
  return meters / 1000
}

/**
 * Format speed (km/h or mph)
 * @param kmh Speed in km/h
 * @param unit Target unit ('km' or 'mi')
 * @returns Formatted speed string
 */
export function formatSpeed(kmh: number, unit: DistanceUnit = 'km'): string {
  if (unit === 'mi') {
    const mph = kmh * 0.621371
    return `${Math.round(mph)} mph`
  }
  return `${Math.round(kmh)} km/h`
}

/**
 * Format fuel consumption (L/100km or MPG)
 * @param litersPer100km Fuel consumption in L/100km
 * @param unit Target unit ('km' or 'mi')
 * @returns Formatted consumption string
 */
export function formatFuelConsumption(litersPer100km: number, unit: DistanceUnit = 'km'): string {
  if (unit === 'mi') {
    // Convert L/100km to MPG (US)
    // MPG = 235.214 / (L/100km)
    const mpg = 235.214 / litersPer100km
    return `${mpg.toFixed(1)} MPG`
  }
  return `${litersPer100km.toFixed(1)} L/100km`
}
